import React, { useMemo } from 'react';
import Select from 'react-select';

const FilterPanel = ({ filters, onFiltersChange, uniqueFields = [] }) => {
  // Create field options from unique fields in the positions data
  // This is much faster than loading all 1186 doctoral fields
  const fieldOptions = useMemo(() => {
    return uniqueFields.map(field => ({
      value: field,
      label: field
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [uniqueFields]);

  const handleFieldChange = (selectedOptions) => {
    const selectedValues = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    onFiltersChange({ ...filters, fields: selectedValues });
  };

  const handleCountryChange = (e) => {
    onFiltersChange({ ...filters, country: e.target.value });
  };

  const handlePrestigeMinChange = (e) => {
    onFiltersChange({ ...filters, prestigeMin: e.target.value });
  };

  const handlePrestigeMaxChange = (e) => {
    onFiltersChange({ ...filters, prestigeMax: e.target.value });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
      {/* Fields Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Filter by Fields
        </label>
        <Select
          isMulti
          options={fieldOptions}
          value={fieldOptions.filter(opt => filters.fields.includes(opt.value))}
          onChange={handleFieldChange}
          className="basic-multi-select"
          classNamePrefix="select"
          placeholder="Select fields..."
          maxMenuHeight={200}
        />
      </div>

      {/* Country Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Filter by Country
        </label>
        <input
          type="text"
          placeholder="Enter country..."
          value={filters.country}
          onChange={handleCountryChange}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
        />
      </div>

      {/* Prestige Score Range */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Prestige Score Range
        </label>
        <div className="flex gap-2">
          <input
            type="number"
            placeholder="Min"
            value={filters.prestigeMin}
            onChange={handlePrestigeMinChange}
            min="0"
            max="1"
            step="0.01"
            className="w-1/2 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
          />
          <input
            type="number"
            placeholder="Max"
            value={filters.prestigeMax}
            onChange={handlePrestigeMaxChange}
            min="0"
            max="1"
            step="0.01"
            className="w-1/2 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
          />
        </div>
      </div>

      {/* At Risk Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          At Risk Positions
        </label>
        <select
          value={filters.atRisk}
          onChange={(e) => onFiltersChange({ ...filters, atRisk: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">All Positions</option>
          <option value="atRisk">At Risk Only</option>
          <option value="notAtRisk">Not At Risk Only</option>
        </select>
      </div>

      {/* Reported Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Reported Positions
        </label>
        <select
          value={filters.reported}
          onChange={(e) => onFiltersChange({ ...filters, reported: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">All Positions</option>
          <option value="reported">Reported Only</option>
          <option value="notReported">Not Reported Only</option>
        </select>
      </div>

      {/* Link Status Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Link Status
        </label>
        <select
          value={filters.linkStatus}
          onChange={(e) => onFiltersChange({ ...filters, linkStatus: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">All Statuses</option>
          <option value="valid">Valid Links</option>
          <option value="invalid">Link Error</option>
          <option value="error">Link Error</option>
          <option value="not_checked">Not Checked</option>
        </select>
      </div>

      {/* Created By Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Created By
        </label>
        <select
          value={filters.createdBy}
          onChange={(e) => onFiltersChange({ ...filters, createdBy: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">All Types</option>
          <option value="scraper">Scraper</option>
          <option value="professor">Professor</option>
          <option value="student-submission">Student Submission</option>
        </select>
      </div>

      {/* Clear Filters Button */}
      {(filters.fields.length > 0 || filters.country || filters.prestigeMin || filters.prestigeMax || filters.atRisk || filters.reported || filters.linkStatus || filters.createdBy) && (
        <div className="md:col-span-7">
          <button
            onClick={() => onFiltersChange({ fields: [], country: '', prestigeMin: '', prestigeMax: '', atRisk: '', reported: '', linkStatus: '', createdBy: '' })}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Clear All Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
